import React from 'react'
import Layout from '../../components/Layout/Layout'
import { Button, Container, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { toast } from 'react-toastify'
import {
  getAdSamlRedirectUrl,
  getAdSamlRedirectUrlServer
} from '@srca-b2b/kp5-ui-common/utils/authAPIs'
import { getMaintenanceMode } from '@srca-b2b/kp5-ui-common/utils/generalAPIs'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

import classNames from 'classnames'
import styles from './login.module.scss'
import PlainHeader from 'components/PlainHeader/PlainHeader'
import { correctPTlocale } from '@srca-b2b/kp5-ui-common/utils/helpers'

const FederatedLogin = ({ responseData }) => {
  const router = useRouter()
  const { t } = useTranslation('common')

  const schema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Required')
  })

  const handleError = async (data) => {
    // Here we handle special error codes and show error messages.
    let fullMessage
    switch (data.error) {
      case 'LOGIN_MUST_USE_SAMSUNG_ACCOUNT':
        fullMessage = (
          <div>
            <p className="toastTitle">{t('Identity_provider_not_set_up')}</p>
            {t('Use_your')}{' '}
            <Link href={'/en/login/samsung'}>
              <a>{t('Samsung_account_to_sign_in')}</a>
            </Link>{' '}
            {t('instead')}.
          </div>
        )
        break
      case 'LOGIN_ACCOUNT_DOES_NOT_EXIST':
        fullMessage = (
          <div>
            Account has not been setup, please{' '}
            <Link href={'/en/register'}>
              <a>register</a>
            </Link>
            .
          </div>
        )
        break
    }
    toast.error(fullMessage ? fullMessage : data.message, { className: 'notification' })
  }

  const onSubmit = async (formValues) => {
    const response = await getAdSamlRedirectUrl(formValues.email)
    if (response.statusCode === 200) {
      router.push(response.data.redirectUrl)
    } else {
      handleError(response)
    }
  }

  if (responseData) {
    handleError(responseData)
  }

  return (
    <Layout containerClassName="pt-0">
      <PlainHeader />
      <Container className={classNames(styles.sso)}>
        <div className={styles.content}>
          <h2 className={classNames(styles.heading, 'pageH2')}>{t('Sign_in')}</h2>
          <p className="font-weight-bold text-center mb-0">{t('Sign_in_desc_text')}</p>
          <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={{
              email: ''
            }}
          >
            {({ handleSubmit, handleChange, values, errors, isSubmitting }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group controlId="ga-kp-SignIn-SSO-Email" className="mb-0">
                  <Form.Label>{t('Email')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    placeholder={'name@example.com'}
                  />
                </Form.Group>
                <Button
                  id="ga-kp-SignIn-SSO-Next"
                  className={classNames(styles.btnNext, 'btn-md')}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {t('Next')}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </Container>
    </Layout>
  )
}

export const getServerSideProps = async ({ req, query, locale }) => {
  // Redirect to maintenance page if maintenanceMode
  const maintenanceResponse = await getMaintenanceMode(req.headers.cookie)
  if (maintenanceResponse.statusCode == 200 && maintenanceResponse.data) {
    return {
      redirect: {
        destination: '/en/maintenance',
        permanent: false
      }
    }
  }

  const language = correctPTlocale(locale)
  const props = {
    ...(await serverSideTranslations(language, ['common']))
  }

  let queryEmail = query.email
  if (queryEmail) {
    const response = await getAdSamlRedirectUrlServer(req.headers.cookie, queryEmail)
    if (response.statusCode === 200) {
      return {
        redirect: {
          destination: response.data.redirectUrl,
          permanent: false
        }
      }
    } else {
      return { props: { ...props, responseData: response } }
    }
  }
  return { props }
}

export default FederatedLogin
